import React, { useEffect, useState } from "react";

import { MdmDeviceDto, MdmObjectDto, useGetDevicesPagedLazyQuery, useGetDevicesPagedQuery } from "gql/generated/types";
import { DataTablePFSEvent } from "primereact/datatable";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import { TableColumn } from "utils/types";
import { ProgressSpinner } from "primereact/progressspinner";
import LazyTable from "../components/LazyTable";
import MapWrapper from "../components/MapWrapper";
import ObjectMapContent from "../components/ObjectMapContent";
import useProjectContext from "../hooks/useProjectContext";
import { errorToaster, handleDeviceStatus } from "../utils/utils";
import { LazyState } from "../utils/types";
import { DEVICES_TABLE_NAME } from "../utils/const";

const Devices = () => {
  const {projectId, project, userSettings} = useProjectContext();
  const userTableDetails = () =>
    userSettings?.userSettings.tablesDetails?.find(r => r?.table === DEVICES_TABLE_NAME);
  const [lazyState, setLazyState] = useState<LazyState>({
    first: 0,
    rows: userTableDetails()?.rows || 10,
    page: 1,
    sortField: "identifier",
    sortOrder: 1,
    multiSortMeta: [],
    filters: {},
    andFilters: [],
    internalFilters: [],
  });

  const [getNextDevices,{data: deviceData, loading }] = useGetDevicesPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  useEffect(() => {
    if(!deviceData && !loading) {
      getNextDevices({
        variables: {
          input: {
            projectId: projectId!,
            take: userTableDetails()?.rows || 10,
            skip: 0
          }
        },
      })
    }
  }, [deviceData]);

  const edit = (rowData: MdmDeviceDto) => (
    <Link to={`${rowData.id}`} title="Device Info">
      {rowData.identifier}
    </Link>
  );
  
  const handleLastMeasurementDate = (rowData: MdmDeviceDto) =>
    rowData.status.lastMeasurementDate &&
    parseToLocalZoneDateTime(rowData.status.lastMeasurementDate).format(DEFAULT_DATE_FORMATTER);

  const objectCodeTemplate = (rowData: MdmDeviceDto) => (
    <Link to={`/mdm/${projectId}/objects/${rowData.objectRelation?.mdmObject?.id}`}>
      {rowData.objectRelation?.mdmObject?.identifier}
    </Link>
  );
  const tableData = (deviceData?.mdmDevicesByProjectIdPaged.items || []) as MdmDeviceDto[];

  const columns: TableColumn<MdmDeviceDto>[] = [
    { field: "identifier", header: "Identifier", body: edit, sortable: true },
    {
      field: "objectRelation.mdmObject.identifier",
      header: "Object Code",
      body: objectCodeTemplate,
      sortable: true,
    },
    {
      field: "objectRelation.mdmObject.address.fullAddress",
      header: "Object Address",
      sortable: true,
    },
    {
      field: "objectRelation.roleInObject",
      header: "Type",
      body: rowData =>
        project?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes?.find(
          type => type?.id === rowData.objectRelation?.roleInObject
        )?.title ?? "",
      sortable: true,
    },
    {
      field: "config.title",
      header: "Config",
      sortable: true,
    },
    {
      field: "status.status",
      header: "Status",
      body: handleDeviceStatus,
      sortable: true,
      hideInFile: true,
    },
    {
      field: "status.lastMeasurementDate",
      header: "Last Measurement Date",
      body: handleLastMeasurementDate,
      sortable: true,
    },
  ];

  return (
    <TabView panelContainerClassName="p-0 sm:p-4">
      <TabPanel header="Table">
        <LazyTable
          columns={columns as TableColumn<Partial<MdmDeviceDto>>[]}
          data={tableData}
          tableName={DEVICES_TABLE_NAME}
          loading={loading}
          onLazyEvent={setLazyState}
          lazyState={lazyState}
          totalItems={deviceData?.mdmDevicesByProjectIdPaged.pageInfo?.total || 0}
          getNextQuery={getNextDevices}
        />
      </TabPanel>
      <TabPanel header="Map">
        <DeviceProvider lazyState={lazyState} records={deviceData?.mdmDevicesByProjectIdPaged.pageInfo?.total ?? 100} />
      </TabPanel>
    </TabView>
  );
};

export default Devices;

type DeviceProviderProps = {
  lazyState: DataTablePFSEvent;
  records: number;
};
const DeviceProvider: React.FC<DeviceProviderProps> = ({lazyState,records}) => {
  const {projectId} = useProjectContext();

  const { data, loading } = useGetDevicesPagedQuery({
    variables: {
      input: {
        projectId,
        skip: 0,
        take: records,
        andFilters: lazyState.andFilters,
      },
    },
  });

  return (
    <MapWrapper>
      {loading && <ProgressSpinner className='mt-8 w-full' style={{zIndex: 400}}/>}
      <ObjectMapContent objects={[...new Set(data?.mdmDevicesByProjectIdPaged.items.map(t => t?.objectRelation?.mdmObject! as MdmObjectDto))]} />
    </MapWrapper>
  );
}